import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Archive, ArrowLeft, BellOff, Megaphone, MessageCircle, Pin, Plus, Search, Users } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { fdate, fdatetime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useConfig } from "@/context/ConfigContext";
import { enqueue, dequeue, queued } from "@/lib/chatQueue";
import { EmptyState, Loader, PageTitle } from "@/components/common";
import { Bubble } from "@/components/chat/Bubble";
import { Composer } from "@/components/chat/Composer";
import { ConvInfo, ConvPhoto } from "@/components/chat/ConvInfo";

function NewGroup({ open, onOpenChange }) {
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [q, setQ] = useState("");
  const [found, setFound] = useState([]);
  const [picked, setPicked] = useState([]);
  useEffect(() => {
    api.get("/members", { params: { q } }).then(({ data }) => setFound(data.items)).catch(() => {});
  }, [q]);
  const create = async () => {
    try {
      const { data } = await api.post("/conversations/group", { title, description: desc, member_ids: picked.map((p) => p.id) });
      onOpenChange(false);
      if (data.pending) {
        toast.success(data.message);
        nav("/app/moi?tab=demandes");
        return;
      }
      nav(`/app/messages/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogTitle>Nouveau groupe</DialogTitle>
        <input data-testid="group-title-input" className="mr-input" placeholder="Nom du groupe" value={title} onChange={(e) => setTitle(e.target.value)} />
        <textarea data-testid="group-description-input" className="mr-input min-h-[60px]" placeholder="Description (facultatif)" value={desc} onChange={(e) => setDesc(e.target.value)} />
        <div className="flex flex-wrap gap-1">{picked.map((p) => <button key={p.id} onClick={() => setPicked(picked.filter((x) => x.id !== p.id))} className="text-xs px-2 py-1 rounded-full bg-mr-raised border border-mr-border">{p.name} ×</button>)}</div>
        <input data-testid="group-member-search" className="mr-input" placeholder="Ajouter des membres…" value={q} onChange={(e) => setQ(e.target.value)} />
        <ul className="max-h-48 overflow-y-auto divide-y divide-mr-border">
          {found.filter((m) => !picked.some((p) => p.id === m.id)).map((m) => (
            <li key={m.id}><button data-testid="group-member-option" className="w-full text-left py-2 text-sm" onClick={() => setPicked([...picked, m])}>{m.name} <span className="text-mr-muted">{m.company}</span></button></li>
          ))}
        </ul>
        <button data-testid="group-create-button" className="mr-btn-primary w-full" disabled={title.trim().length < 2} onClick={create}>Créer le groupe ({picked.length + 1})</button>
      </DialogContent>
    </Dialog>
  );
}

function GlobalSearch({ q }) {
  const [res, setRes] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => api.get("/messages/search", { params: { q } }).then(({ data }) => setRes(data)).catch(() => setRes([])), 300);
    return () => clearTimeout(t);
  }, [q]);
  if (!res) return <Loader />;
  if (!res.length) return <EmptyState icon={Search} title="Aucun message" text="Essayez un autre mot." testId="msg-search-empty" />;
  return (
    <ul className="space-y-2">
      {res.map((m) => (
        <li key={m.id}><Link data-testid="msg-search-result" to={`/app/messages/${m.conversation_id}?m=${m.id}`} className="mr-card block p-3">
          <p className="text-xs text-mr-muted">{m.conversation_title} · {fdatetime(m.created_at)}</p>
          <p className="text-sm"><span className="font-medium">{m.sender_name} : </span>{m.text}</p>
        </Link></li>
      ))}
    </ul>
  );
}

const TYPE_ICON = { groupe: Users, annonce: Megaphone, operation: Users };

export function Conversations() {
  const [items, setItems] = useState(null);
  const [archived, setArchived] = useState(false);
  const [q, setQ] = useState("");
  const [newGroup, setNewGroup] = useState(false);
  const load = useCallback(() => api.get("/conversations", { params: { archived } }).then(({ data }) => setItems(data)).catch(() => setItems((v) => v || [])), [archived]);
  useEffect(() => {
    load();
    const onWs = (e) => ["message", "message_update"].includes(e.detail.type) && load();
    window.addEventListener("mr-ws", onWs);
    return () => window.removeEventListener("mr-ws", onWs);
  }, [load]);
  return (
    <div className="mr-fade-up">
      <PageTitle eyebrow="Messages" title="Conversations">
        <button data-testid="new-group-button" className="mr-btn-primary px-3" onClick={() => setNewGroup(true)}><Plus size={16} /> Groupe</button>
      </PageTitle>
      <div className="relative mb-3"><Search size={15} className="absolute left-3 top-3.5 text-mr-muted" />
        <input data-testid="messages-search-input" className="mr-input pl-9" placeholder="Rechercher dans tous les messages" value={q} onChange={(e) => setQ(e.target.value)} /></div>
      <div className="flex gap-1 mb-4 border-b border-mr-border">
        {[[false, "Discussions"], [true, "Archivées"]].map(([v, l]) => (
          <button key={l} data-testid={`messages-tab-${v ? "archived" : "all"}`} onClick={() => setArchived(v)} className={`px-3 h-10 text-sm border-b-2 -mb-px ${archived === v ? "border-mr-accent" : "border-transparent text-mr-muted"}`}>{l}</button>
        ))}
      </div>
      {q.trim().length >= 2 ? <GlobalSearch q={q} /> : items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={archived ? Archive : MessageCircle} title={archived ? "Aucune conversation archivée" : "Aucune conversation"} text="Depuis le Réseau, ouvrez la fiche d'un membre pour lui écrire, ou créez un groupe." />
      ) : (
        <ul className="space-y-2">
          {items.map((c) => {
            const Icon = TYPE_ICON[c.type];
            return (
              <li key={c.id}>
                <Link to={`/app/messages/${c.id}`} data-testid="conversation-row" className="mr-card flex items-center gap-3 p-4 hover:border-mr-text2 transition-colors">
                  <div className="relative"><ConvPhoto conv={c} size={40} />{c.online && <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-mr-accent border-2 border-mr-surface" data-testid="conv-online-dot" />}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex justify-between gap-2">
                      <p className="font-medium truncate flex items-center gap-1.5">{Icon && <Icon size={13} className="text-mr-muted shrink-0" />}{c.title}</p>
                      <span className="text-xs text-mr-muted tabular shrink-0 flex items-center gap-1">
                        {c.state.pinned && <Pin size={11} data-testid="conv-pinned-icon" />}{c.state.muted && <BellOff size={11} data-testid="conv-muted-icon" />}
                        {c.last_message_at ? fdate(c.last_message_at) : ""}</span>
                    </div>
                    <p className="text-sm text-mr-text2 truncate">{c.last_message || "Nouvelle conversation"}</p>
                  </div>
                  {c.unread > 0 && <span data-testid="conv-unread" className="min-w-[20px] h-5 px-1.5 rounded-full bg-mr-accent text-mr-on-accent text-xs font-bold grid place-items-center">{c.unread}</span>}
                </Link>
                <div className="flex justify-end gap-3 text-[11px] text-mr-muted pr-1 pt-1">
                  <button data-testid="conv-pin-toggle" onClick={() => api.post(`/conversations/${c.id}/state`, { pinned: !c.state.pinned }).then(load)}>{c.state.pinned ? "Désépingler" : "Épingler"}</button>
                  <button data-testid="conv-row-archive" onClick={() => api.post(`/conversations/${c.id}/state`, { archived: !c.state.archived }).then(load)}>{c.state.archived ? "Désarchiver" : "Archiver"}</button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      <NewGroup open={newGroup} onOpenChange={setNewGroup} />
    </div>
  );
}

export function JoinGroup() {
  const { token } = useParams();
  const nav = useNavigate();
  const [err, setErr] = useState("");
  useEffect(() => {
    api.post(`/conversations/join/${token}`).then(({ data }) => nav(`/app/messages/${data.id}`, { replace: true })).catch((e) => setErr(errMsg(e)));
  }, [token, nav]);
  return err ? <EmptyState title="Lien invalide" text={err} testId="join-group-error" /> : <Loader />;
}

function Forward({ msg, onClose }) {
  const [convs, setConvs] = useState([]);
  const [sel, setSel] = useState([]);
  useEffect(() => {
    api.get("/conversations").then(({ data }) => setConvs(data)).catch(() => {});
  }, []);
  const send = async () => {
    try {
      await api.post(`/messages/${msg.id}/forward`, { conversation_ids: sel });
      toast.success("Message transféré");
      onClose();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogTitle>Transférer à…</DialogTitle>
        <ul className="max-h-72 overflow-y-auto divide-y divide-mr-border">
          {convs.map((c) => (
            <li key={c.id}><label className="flex items-center gap-3 py-2 text-sm"><input type="checkbox" data-testid="forward-option" className="w-4 h-4" checked={sel.includes(c.id)}
              onChange={(e) => setSel(e.target.checked ? [...sel, c.id].slice(0, 5) : sel.filter((x) => x !== c.id))} />{c.title}</label></li>
          ))}
        </ul>
        <button data-testid="forward-send-button" className="mr-btn-primary w-full" disabled={!sel.length} onClick={send}>Transférer ({sel.length}/5)</button>
      </DialogContent>
    </Dialog>
  );
}

const lastSeen = (c) => (c.online ? "en ligne" : c.last_seen_at ? `vu ${fdatetime(c.last_seen_at)}` : "");

export function Thread() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { me } = useAuth();
  const { settings } = useConfig() || {};
  const myId = me.user.id;
  const [conv, setConv] = useState(null);
  const [msgs, setMsgs] = useState(null);
  const [error, setError] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [editing, setEditing] = useState(null);
  const [forwarding, setForwarding] = useState(null);
  const [info, setInfo] = useState(false);
  const [typing, setTyping] = useState("");
  const [stickers, setStickers] = useState([]);
  const [highlight, setHighlight] = useState(params.get("m"));
  const end = useRef(null);
  const typingTimer = useRef(null);

  const loadConv = useCallback(() => api.get(`/conversations/${id}`).then(({ data }) => setConv({ ...data, me_id: myId })).catch((e) => setError(errMsg(e))), [id, myId]);
  const loadMsgs = useCallback((around) => api.get(`/conversations/${id}/messages`, { params: around ? { around } : {} }).then(({ data }) => {
    const pending = queued(id).map((q) => ({ ...q.payload, id: q.payload.client_id, sender_id: myId, created_at: new Date(q.at).toISOString(), receipt: "en_attente", reactions: {}, media_file_ids: q.payload.media_file_ids || [], status: "envoye" }));
    setMsgs([...data, ...pending.filter((p) => !data.some((d) => d.client_id === p.client_id))]);
  }).catch((e) => setError(errMsg(e))), [id, myId]);

  useEffect(() => {
    loadConv();
    loadMsgs(params.get("m"));
    api.get("/stickers").then(({ data }) => setStickers(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    const onWs = (e) => {
      const d = e.detail;
      if (d.conversation_id !== id) return;
      if (d.type === "message") {
        setMsgs((m) => [...(m || []).filter((x) => x.id !== d.message.id), d.message]);
        setTyping("");
        api.get(`/conversations/${id}/messages`).catch(() => {});
      }
      if (d.type === "message_update") {
        if (conv?.pinned?.some((p) => p.id === d.id)) loadConv();
        setMsgs((m) => (m || []).map((x) => (x.id !== d.id ? x : { ...x, ...(d.content !== undefined ? { content: d.content, edited_at: new Date().toISOString() } : {}),
          ...(d.deleted ? { status: "supprime" } : {}), ...(d.reactions ? { reactions: d.reactions } : {}) })));
      }
      if (d.type === "read") setMsgs((m) => (m || []).map((x) => (x.sender_id === myId && conv?.type === "direct" ? { ...x, receipt: "lu" } : x)));
      if (d.type === "typing") {
        setTyping(conv?.type === "direct" ? "écrit…" : `${d.name} écrit…`);
        clearTimeout(typingTimer.current);
        typingTimer.current = setTimeout(() => setTyping(""), 4000);
      }
    };
    const onSent = (e) => e.detail.convId === id && setMsgs((m) => (m || []).map((x) => (x.client_id === e.detail.message.client_id ? e.detail.message : x)));
    window.addEventListener("mr-ws", onWs);
    window.addEventListener("mr-queue-sent", onSent);
    return () => {
      window.removeEventListener("mr-ws", onWs);
      window.removeEventListener("mr-queue-sent", onSent);
    };
  }, [id, myId, conv?.type, conv?.pinned, loadConv]);

  useEffect(() => {
    if (highlight && msgs) {
      document.getElementById(`msg-${highlight}`)?.scrollIntoView({ block: "center" });
      const t = setTimeout(() => setHighlight(null), 2500);
      return () => clearTimeout(t);
    }
    end.current?.scrollIntoView({ block: "end" });
    return undefined;
  }, [msgs, highlight]);

  const jump = (mid) => {
    if (document.getElementById(`msg-${mid}`)) setHighlight(mid);
    else loadMsgs(mid).then(() => setHighlight(mid));
  };

  const send = async (payload) => {
    if (editing) {
      await api.patch(`/messages/${editing.id}`, { content: payload.content }).catch((e) => toast.error(errMsg(e)));
      setEditing(null);
      return;
    }
    const body = { ...payload, reply_to_id: replyTo?.id, client_id: crypto.randomUUID?.() || `${Date.now()}-${Math.random()}` };
    setReplyTo(null);
    const temp = { ...body, id: body.client_id, sender_id: myId, created_at: new Date().toISOString(), receipt: "en_attente", reactions: {}, media_file_ids: body.media_file_ids || [], status: "envoye", meta: body.meta || {}, content: body.content || "" };
    setMsgs((m) => [...(m || []), temp]);
    try {
      const { data } = await api.post(`/conversations/${id}/messages`, body);
      setMsgs((m) => m.map((x) => (x.id === temp.id ? data : x)));
    } catch (e) {
      if (!e.response || e.response.status >= 500) {
        enqueue(id, body);
        toast("Hors connexion : le message partira automatiquement.");
      } else {
        dequeue(body.client_id);
        setMsgs((m) => m.filter((x) => x.id !== temp.id));
        toast.error(errMsg(e));
      }
    }
  };

  const actions = {
    jump,
    reply: (m) => { setEditing(null); setReplyTo(m); },
    edit: (m) => { setReplyTo(null); setEditing(m); },
    forward: (m) => setForwarding(m),
    copy: (m) => { navigator.clipboard?.writeText(m.content); toast.success("Copié"); },
    pin: (m) => api.post(`/messages/${m.id}/pin`).then(({ data }) => { toast.success(data.pinned ? "Message épinglé" : "Message désépinglé"); loadConv(); }).catch((e) => toast.error(errMsg(e))),
    react: (m, emoji) => api.post(`/messages/${m.id}/react`, { emoji }).then(({ data }) => setMsgs((ms) => ms.map((x) => (x.id === m.id ? { ...x, reactions: data.reactions, my_reactions: data.mine ? [...(x.my_reactions || []), emoji] : (x.my_reactions || []).filter((e) => e !== emoji) } : x)))).catch((e) => toast.error(errMsg(e))),
    remove: (m, scope) => api.delete(`/messages/${m.id}`, { params: { scope } }).then(() => setMsgs((ms) => (scope === "me" ? ms.filter((x) => x.id !== m.id) : ms.map((x) => (x.id === m.id ? { ...x, status: "supprime" } : x))))).catch((e) => toast.error(errMsg(e))),
    report: (m) => {
      const reason = window.prompt("Motif du signalement ?");
      if (reason && reason.trim().length >= 3) api.post(`/messages/${m.id}/report`, { reason }).then(() => toast.success("Signalement transmis à la modération")).catch((e) => toast.error(errMsg(e)));
    },
  };

  if (error && !msgs) return <EmptyState title="Conversation introuvable" text={error} testId="thread-error" />;
  if (!conv || !msgs) return <Loader />;
  const canPin = conv.type === "direct" || conv.is_admin;
  return (
    <div className="flex flex-col min-h-[calc(100vh-14rem)]">
      <div className="flex items-center gap-2 mb-3">
        <button data-testid="thread-back-button" aria-label="Conversations" className="mr-btn-ghost -ml-3" onClick={() => nav("/app/messages")}><ArrowLeft size={16} /></button>
        <button data-testid="thread-info-button" className="flex items-center gap-2 min-w-0 text-left" onClick={() => setInfo(true)}>
          <ConvPhoto conv={conv} />
          <span className="min-w-0"><span className="font-medium block truncate" data-testid="thread-title">{conv.title}</span>
            <span className="text-xs text-mr-muted block truncate" data-testid="thread-subtitle">{typing || (conv.type === "direct" ? lastSeen(conv) : conv.type === "annonce" ? "Canal d'annonces" : `${conv.participants.length} participants`)}</span></span>
        </button>
      </div>
      {conv.pinned.length > 0 && (
        <div className="mb-3 space-y-1" data-testid="thread-pinned">
          {conv.pinned.map((p) => <button key={p.id} onClick={() => jump(p.id)} className="w-full flex items-center gap-2 text-left text-xs px-3 py-2 bg-mr-surface border border-mr-border"><Pin size={12} className="text-mr-accent" /> <span className="truncate">{p.text.replace(/[*_~]/g, "")}</span></button>)}
        </div>
      )}
      <div className="flex-1 space-y-3 md:px-10 pb-6" data-testid="thread-messages">
        {msgs.map((m) => <Bubble key={m.id} m={m} mine={m.sender_id === myId} showName={conv.type !== "direct"} canPin={canPin} actions={actions} highlight={highlight === m.id} />)}
        <div ref={end} />
      </div>
      <Composer conv={conv} onSend={send} replyTo={replyTo} editing={editing} onCancel={() => { setReplyTo(null); setEditing(null); }}
        stickers={stickers} voiceMax={Number(settings?.["messages.voice_max_seconds"]) || 180} />
      {forwarding && <Forward msg={forwarding} onClose={() => setForwarding(null)} />}
      <ConvInfo conv={conv} open={info} onOpenChange={setInfo} reload={loadConv} onJump={jump} />
    </div>
  );
}
